import * as Sentry from '@sentry/react';
import { useStoreActions } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { QueryKeys } from '../queryKeys';
import { useQueryState } from '../useQueryState';
import { buildDiffPayload, diffActivityLogs } from './useActivityLog.helpers';
const EMPTY_ACTIVITY_LOG = [];
const fetchSingle = (api, relationshipId, ffOpen)=>ffOpen ? api.activityLog.get(relationshipId) : api.activityLog.getOld(relationshipId);
const fetchAndCompare = async (api, relationshipId, ffOpen)=>{
    const [oldOutcome, newOutcome] = await Promise.allSettled([
        api.activityLog.getOld(relationshipId),
        api.activityLog.get(relationshipId)
    ]);
    if (oldOutcome.status === 'fulfilled' && newOutcome.status === 'fulfilled') {
        const diff = diffActivityLogs(oldOutcome.value, newOutcome.value);
        if (diff != null) {
            api.activityLog.postDiff(buildDiffPayload({
                relationshipId,
                ffOpen,
                diff,
                oldResult: oldOutcome.value,
                newResult: newOutcome.value,
                timestamp: new Date().toISOString()
            })).catch(()=>undefined);
        }
    } else {
        if (oldOutcome.status === 'rejected') {
            Sentry.captureException(oldOutcome.reason, {
                extra: {
                    hook: 'useActivityLog',
                    side: 'old',
                    relationshipId
                }
            });
        }
        if (newOutcome.status === 'rejected') {
            Sentry.captureException(newOutcome.reason, {
                extra: {
                    hook: 'useActivityLog',
                    side: 'new',
                    relationshipId
                }
            });
        }
    }
    const selectedOutcome = ffOpen ? newOutcome : oldOutcome;
    if (selectedOutcome.status === 'rejected') {
        throw selectedOutcome.reason;
    }
    return selectedOutcome.value;
};
export const useActivityLog = function useActivityLog(relationshipId) {
    let { refreshOnHookMount = false } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
    const { api } = useStoreActions();
    const { ACTIVITY_LOG_V2, ACTIVITY_LOG_DUAL_CALL } = useFeatureFlags();
    const ffOpen = !!ACTIVITY_LOG_V2;
    const dualCallEnabled = !!ACTIVITY_LOG_DUAL_CALL;
    const { data, isLoading, error, refresh } = useQueryState({
        queryKey: [
            QueryKeys.ActivityLog,
            relationshipId,
            ffOpen,
            dualCallEnabled
        ],
        queryFn: ()=>dualCallEnabled ? fetchAndCompare(api, relationshipId, ffOpen) : fetchSingle(api, relationshipId, ffOpen),
        enabled: !!relationshipId,
        refreshOnHookMount
    });
    return {
        activityLog: data !== null && data !== void 0 ? data : EMPTY_ACTIVITY_LOG,
        isLoading,
        error,
        refresh
    };
};
